import { useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellOff, Settings } from 'lucide-react';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { useRecentNotifications } from '@/hooks/useNotifications';
import { useNotificationStore, useUnreadCount } from '@/stores/notification.store';
import { notificationService } from '@/services/notification.service';
import { notificationSocketService } from '@/services/notification-socket.service';
import { NotificationItem } from './NotificationItem';

const LEARNER_DROPDOWN_LIMIT = 5;
const ADMIN_DROPDOWN_LIMIT = 10;

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  isLearner?: boolean;
}

export function NotificationDropdown({ isOpen, onClose, isLearner = false }: NotificationDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownLimit = isLearner ? LEARNER_DROPDOWN_LIMIT : ADMIN_DROPDOWN_LIMIT;
  const recentNotifications = useRecentNotifications(dropdownLimit);
  const isLoading = useNotificationStore((s) => s.isLoading);
  const syncFromServer = useNotificationStore((s) => s.syncFromServer);
  const preferences = useNotificationStore((s) => s.preferences);
  // Unread count is DB-driven — always read from store (set by server responses).
  const unreadCount = useUnreadCount();

  // All actions hit the API first, then resync from DB — no optimistic local writes.
  const markAsRead = useCallback(async (notificationId: string) => {
    notificationSocketService.emitMarkAsRead(notificationId);
    await notificationService.markAsRead(notificationId);
    await syncFromServer();
  }, [syncFromServer]);

  const markAllAsRead = useCallback(async () => {
    notificationSocketService.emitMarkAllAsRead();
    await notificationService.markAllAsRead();
    await syncFromServer();
  }, [syncFromServer]);

  // Popup X — clears from popup via DB, then resync.
  const dismiss = useCallback(async (notificationId: string) => {
    await notificationService.clearFromPopup(notificationId);
    await syncFromServer();
  }, [syncFromServer]);

  const basePath = isLearner ? '/learn' : '/admin';

  // Closing the dropdown is a no-op — items stay unread until the user
  // explicitly reads them (click) or hits "Mark all as read".
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        handleClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Show a banner when the push toggle is OFF in the user's preferences.
  // The banner's "Enable" button deep-links to Settings to flip it on.
  // Boolean-only check — no OneSignal readiness gate, so the banner also
  // appears on tenants/environments where OneSignal hasn't initialized.
  const showPushDisabledBanner = preferences?.channels?.push?.enabled !== true;

  return (
    <div
      ref={dropdownRef}
      className="fixed inset-x-3 top-14 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 mt-2 sm:w-96 max-h-[calc(100vh-100px)] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
    >
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Mark all as read */}
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                Mark all as read
              </button>
            )}

            {/* Settings link — available for both admin and learner */}
            <Link
              to={`${basePath}/notifications/settings`}
              onClick={handleClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Notification settings"
            >
              <Settings className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Push-disabled banner */}
      {showPushDisabledBanner && (
        <div className="bg-amber-50 border-b border-amber-200 p-3 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <BellOff className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">Push notifications are off</p>
            <p className="text-xs text-amber-700 mt-0.5 leading-snug">
              Enable to get real-time alerts on this device.
            </p>
          </div>
          <Link
            to={`${basePath}/notifications/settings`}
            onClick={handleClose}
            className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 px-3 py-1.5 rounded-md transition-colors flex-shrink-0 self-center whitespace-nowrap"
          >
            Enable
          </Link>
        </div>
      )}

      {/* Notifications list */}
      <div className="overflow-y-auto max-h-[400px] custom-scrollbar">
        {isLoading ? (
          <div className="p-3 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <SkeletonLoader type="avatar" className="flex-shrink-0 w-10 h-10" />
                <div className="flex-1 space-y-2">
                  <SkeletonLoader type="text" className="h-4 w-3/4" />
                  <SkeletonLoader type="text" className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : recentNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Bell className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-center">No notifications yet</p>
            <p className="text-gray-400 text-sm text-center mt-1">
              We'll notify you when something arrives
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentNotifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onDismiss={dismiss}
                showActions
                compact
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t border-gray-100 p-3">
        <Link
          to={isLearner ? '/learn/notifications' : '/admin/notifications/my-notifications'}
          onClick={handleClose}
          className="block w-full text-center text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 py-2 rounded-lg transition-colors"
        >
          {isLearner ? 'My Notifications' : 'Manage Notifications'}
        </Link>
      </div>
    </div>
  );
}

export default NotificationDropdown;
